/* @flow */
import * as React from "react";
import {
  withColorsProvider,
  type ColorSet,
} from "nx-components/ColorsProvider";
import Tappable, { simpleActiveBackgroundColor } from "nx-components/Tappable";
import prefixStyles from "nx-components/utils/prefixStyles";
import Device from "nexia_mobile/models/device";
import BatteryIndicator from "nexia_mobile/components/BatteryIndicator";
import MinimalBatteryIndicator from "nexia_mobile/components/MinimalBatteryIndicator";
import Battery from "nexia_mobile/models/mixins/device_features/battery";
import { createTileControlsForDevice } from "nexia_mobile/components/Controls/createControlsForModel";
import Icons from "nexia_mobile/icons"; // some icon helper methods
import IconGroup from "nexia_mobile/components/IconGroup";
import DarkModeInverter from "nexia_mobile/components/DarkModeInverter";
import parseIconsFromServer from "nexia_mobile/parseIconsFromServer";
import IconGroupTileWrapper from "nexia_mobile/components/Tiles/IconGroupTileWrapper";
import TileBody from "nexia_mobile/components/Tiles/TileBody";
import TileSpinner from "nexia_mobile/components/Tiles/TileSpinner";
import { history } from "nexia_mobile/history";
import { getTransitionPromise } from "nexia_mobile/components/Routing/HistoryPageTransitionController";

function getColorWithOpacity(color, opacity) {
  if (!color || !(color[0] === "#")) {
    return color;
  }
  const _r = Number.parseInt(color.slice(1, 3), 16);
  const _g = Number.parseInt(color.slice(3, 5), 16);
  const _b = Number.parseInt(color.slice(5, 7), 16);
  return `rgba(${_r},${_g},${_b},${opacity})`;
}

type Props = {
  model: Device,
  tileStyles: {
    contents: Object,
    icon: Object,
    title: Object,
    status: Object,
  },
  colors: ColorSet,
};

type State = {
  spinnerShown: boolean,
};

class DeviceTile extends React.Component<Props, State> {
  state = {
    spinnerShown: false,
  };

  _isMounted: boolean;

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  _showSpinner = () => {
    if (this._isMounted) {
      this.setState({ spinnerShown: true });
    }
  };

  _hideSpinner = () => {
    if (this._isMounted) {
      this.setState({ spinnerShown: false });
    }
  };

  _handleTap = () => {
    const { model } = this.props;
    history.push("/app/devices/details", { device: model });
  };

  _deviceStateColor(modifiers: Array<string>) {
    const { colors } = this.props;

    const modifiersObj = modifiers.reduce((obj, modifier) => {
      obj[modifier] = true;
      return obj;
    }, {});

    if (modifiersObj.emergency) {
      return colors.RED;
    }

    if (
      modifiersObj.alert ||
      modifiersObj.insecure ||
      modifiersObj.disconnected
    ) {
      return colors.RED;
    }

    if (modifiersObj.warning) {
      return colors.WARNING;
    }

    if (modifiersObj.heat) {
      return colors.HEAT;
    }

    if (modifiersObj.cool) {
      return colors.COOL;
    }

    return colors.TEXT_SECONDARY;
  }

  render() {
    const { model, tileStyles, colors } = this.props;
    const { spinnerShown } = this.state;

    const styles = prefixStyles({
      deviceImageHeader: {
        color: colors.HEADER_BACKGROUND,
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
        position: "absolute",
        top: "0",
      },
      title: {
        padding: "0.5em",
        fontSize: "0.85em",
      },
      iconGroup: {
        fontSize: "2.5em",
      },
      verticalCenter: {
        width: "100%",
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
      },
      ellipsis: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    });

    const deviceName = model.get("name");
    const deviceIcon = model.get("icon");

    const currentStatus = _.str.capitalize(model.get("status"));
    const statusModifiers = Icons.getIconModifiers(model.get("icon"));

    const hasBattery =
      model.hasFeature(Battery) && model.get("battery_label") != null;
    const batteryIcon = model.get("battery_icon");
    const batteryLabel =
      model.get("battery_label") === "Unknown"
        ? "?"
        : model.get("battery_label");
    const batteryLevel = model.get("battery_level");

    const deviceImageSrc = model.get("imageSrc");

    const controls = createTileControlsForDevice(model, {
      showSpinner: this._showSpinner,
      hideSpinner: this._hideSpinner,
    });

    let icons = parseIconsFromServer(deviceIcon, {
      showThermostatTemperatureModifier: true,
    });

    return (
      <Tappable onTap={this._handleTap} holdActiveUntil={getTransitionPromise}>
        {({ active, tapTargetRefHandler }) => (
          <div
            data-component-name="DeviceTile"
            className="tile-contents"
            data-status={currentStatus}
            style={{
              ...tileStyles.contents,
              backgroundColor: simpleActiveBackgroundColor(active),
            }}
          >
            {deviceImageSrc ? (
              <DarkModeInverter>
                {({ maybeInvert }) => (
                  <TileBody
                    backgroundStyle={{
                      backgroundImage: `url("${deviceImageSrc}")`,
                      backgroundColor: "black",
                      backgroundSize: "cover",
                      opacity: active ? "0.5" : "",
                      ...maybeInvert,
                    }}
                    innerRef={tapTargetRefHandler}
                    hasControls={Boolean(controls)}
                    header={
                      <div
                        className="tile-title"
                        style={{
                          ...tileStyles.title,
                          ...styles.deviceImageHeader,
                          background: getColorWithOpacity(
                            this._deviceStateColor(statusModifiers),
                            0.5
                          ),
                          ...maybeInvert,
                        }}
                      >
                        <div style={styles.ellipsis}>{deviceName}</div>
                        {hasBattery ? (
                          <MinimalBatteryIndicator
                            icon={batteryIcon}
                            style={{ fontSize: "0.85em" }}
                          />
                        ) : null}
                      </div>
                    }
                  />
                )}
              </DarkModeInverter>
            ) : (
              <TileBody
                innerRef={tapTargetRefHandler}
                hasControls={Boolean(controls)}
                header={
                  hasBattery ? (
                    <BatteryIndicator
                      icon={batteryIcon}
                      label={batteryLabel}
                      level={batteryLevel}
                    />
                  ) : null
                }
              >
                <IconGroupTileWrapper values={icons} unique>
                  <IconGroup style={styles.iconGroup} values={icons} unique />
                </IconGroupTileWrapper>
                <div className="tile-title" style={tileStyles.title}>
                  {deviceName}
                </div>
                <div
                  className={`tile-status ${statusModifiers.join(" ")}`}
                  style={tileStyles.status}
                >
                  {currentStatus}
                </div>
              </TileBody>
            )}
            {controls}
            {spinnerShown ? <TileSpinner /> : null}
          </div>
        )}
      </Tappable>
    );
  }
}

export default withColorsProvider(DeviceTile);



// WEBPACK FOOTER //
// ./src/components/Tiles/DeviceTile.jsx